import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { watchInvoices } from "../../services/invoices";
import "./OwnerLayout.css";
import { invoiceLabel } from "../../services/invoiceLabel";

const PERIODS = [
  { key: "current",  label: "Current month" },
  { key: "prior",    label: "Prior month" },
  { key: "ytd",      label: "Year to date" },
  { key: "year",     label: "Full year" },
];

// Which job dates (YYYY-MM-DD strings) fall in the period. Everything here is
// counted by the JOB's date — billing, collections and guard pay alike — so
// a check written in November for an October job counts in October.
function inPeriod(ymd, period) {
  if (!/^\d{4}-\d{2}-\d{2}/.test(ymd || "")) return false;
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const y = now.getFullYear();
  const thisMonth = `${y}-${pad(now.getMonth() + 1)}`;
  if (period === "current") return ymd.startsWith(thisMonth);
  if (period === "prior") {
    const p = new Date(y, now.getMonth() - 1, 1);
    return ymd.startsWith(`${p.getFullYear()}-${pad(p.getMonth() + 1)}`);
  }
  if (period === "ytd") return ymd.startsWith(String(y)) && ymd.slice(0, 10) <= `${thisMonth}-${pad(now.getDate())}`;
  return ymd.startsWith(String(y));
}

export default function Financials() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("current");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  // Phones get stacked ledger rows instead of the 7-column table.
  const [isPhone, setIsPhone] = useState(typeof window !== "undefined" && window.innerWidth < 700);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // The invoices are the billing record: one per guard per job, with what
  // the broker was billed (total), whether the broker paid (status "paid"),
  // and what was paid to the guard (guardPaidAmount, entered on Guards).
  useEffect(() => {
    if (!userProfile?.companyId) return undefined;
    return watchInvoices(userProfile.companyId, (data) => {
      setInvoices(data);
      setLoading(false);
    });
  }, [userProfile]);

  const rows = invoices.filter((i) => i.status !== "void" && inPeriod(i.scheduledDate, period));
  const billedRows = rows.filter((i) => i.billBroker !== false);
  const sum = (list, f) => list.reduce((s, i) => s + (Number(f(i)) || 0), 0);
  const billed = sum(billedRows, (i) => i.total);
  const collected = sum(billedRows.filter((i) => i.status === "paid"), (i) => i.total);
  const guardPaid = sum(rows, (i) => i.guardPaidAmount);
  const net = collected - guardPaid;

  // One ledger row per job (a 2-guard job has two invoices).
  const byJob = {};
  rows.forEach((i) => {
    const k = i.jobId || i.id;
    if (!byJob[k]) byJob[k] = { key: k, date: i.scheduledDate, jobNumber: invoiceLabel(i), brokerName: i.brokerName, dispatcherName: i.dispatcherName, billed: 0, guardPaid: 0, statuses: new Set() };
    if (i.billBroker !== false) byJob[k].billed += Number(i.total) || 0;
    byJob[k].guardPaid += Number(i.guardPaidAmount) || 0;
    byJob[k].statuses.add(i.status || "draft");
  });
  const ledger = Object.values(byJob).sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const jobStatus = (j) => (j.statuses.size === 1 && j.statuses.has("paid") ? "paid" : j.statuses.has("sent") || j.statuses.has("paid") ? "sent" : "draft");
  const owedRows = ledger.filter((j) => jobStatus(j) !== "paid" && j.billed > 0);

  const byDispatcher = {};
  ledger.forEach((j) => {
    const key = j.dispatcherName || "Unknown";
    if (!byDispatcher[key]) byDispatcher[key] = { jobs: 0, billed: 0 };
    byDispatcher[key].jobs++;
    byDispatcher[key].billed += j.billed;
  });

  const byGuard = {};
  rows.forEach((i) => {
    const key = i.guardName || "No guard assigned";
    if (!byGuard[key]) byGuard[key] = { jobs: 0, paid: 0, unpaid: 0 };
    byGuard[key].jobs++;
    byGuard[key].paid += Number(i.guardPaidAmount) || 0;
    if (i.guardPaidAmount == null && i.payGuard !== false) byGuard[key].unpaid++;
  });

  function formatDate(ymd) {
    if (!ymd) return "—";
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  function fmt(n) { return `$${Number(n || 0).toFixed(2)}`; }
  const statusChip = { paid: "chip-green", sent: "chip-blue", draft: "chip-gray" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Financials</div>
          <div className="page-subtitle">{ledger.length} job{ledger.length !== 1 ? "s" : ""} · {PERIODS.find((p) => p.key === period)?.label} · by job date</div>
        </div>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} style={{ fontSize: 12 }}>
          {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </div>

      <div className="page-body">
        {loading ? <div className="loading-state">Loading...</div> : (
          <>
            {/* Summary cards */}
            {/* Wraps to two per row on a phone so amounts aren't cut off. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 6 }}>
              {[
                { label: "Billed",          value: fmt(billed),    color: "#185FA5" },
                { label: "Collected",       value: fmt(collected), color: "#2d7a2d" },
                { label: "Paid to guards",  value: fmt(guardPaid), color: "#854F0B" },
                { label: "Net (collected − paid)", value: fmt(net), color: net >= 0 ? "#085041" : "#a32d2d" },
              ].map((card) => (
                <div key={card.label} className="panel" style={{ padding: "14px 16px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#888", marginBottom: 4 }}>{card.label.toUpperCase()}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: card.color }}>{card.value}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 16 }}>
              Paid to guards shows what you've entered on the Guards page (💵 Pay). Voided invoices are left out.
            </div>

            {/* Still owed by brokers */}
            {owedRows.length > 0 && (
              <div className="panel" style={{ marginBottom: 16 }}>
                <div className="panel-header" style={{ flexWrap: "wrap", gap: 8 }}>
                  <div className="panel-title">Still owed by brokers</div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <div className="panel-subtitle">{owedRows.length} job{owedRows.length !== 1 ? "s" : ""} · {fmt(billed - collected)}</div>
                    <button className="btn btn-sm" onClick={() => navigate("/owner/invoices")}>Record payments on Invoices ›</button>
                  </div>
                </div>
              </div>
            )}

            {/* Job ledger */}
            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="panel-header">
                <div className="panel-title">Job ledger</div>
              </div>
              {!isPhone && (
              <div style={{
                display: "grid", gridTemplateColumns: "70px 120px 1fr 90px 90px 90px 70px",
                padding: "7px 14px", background: "#f8f8f8", borderBottom: "1px solid #e8e8e8",
                fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
              }}>
                <span>Job date</span><span>Job #</span><span>Broker · Dispatcher</span>
                <span>Billed</span><span>Guard paid</span><span>Net</span><span>Broker</span>
              </div>
              )}
              {ledger.length === 0 ? (
                <div style={{ padding: "20px 14px", fontSize: 12, color: "#bbb", textAlign: "center" }}>No billed jobs in this period</div>
              ) : ledger.map((job) => {
                const jobNet = job.billed - job.guardPaid;
                const st = jobStatus(job);
                if (isPhone) return (
                  <div key={job.key} style={{ padding: "10px 14px", borderBottom: "1px solid #f0f0f0", fontSize: 13 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                      <span style={{ fontWeight: 700, color: "#185FA5" }}>{job.jobNumber || "—"}</span>
                      <span className={`chip ${statusChip[st]}`} style={{ fontSize: 10 }}>{st === "paid" ? "paid" : st === "sent" ? "owed" : "draft"}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#777", marginTop: 2 }}>
                      {formatDate(job.date)} · {[...new Set([job.brokerName, job.dispatcherName].filter(Boolean))].join(" · ")}
                    </div>
                    <div style={{ display: "flex", gap: 14, marginTop: 4, fontSize: 12 }}>
                      <span>Billed <b>{fmt(job.billed)}</b></span>
                      <span style={{ color: "#854F0B" }}>Guard {job.guardPaid ? fmt(job.guardPaid) : "—"}</span>
                      <span style={{ color: jobNet >= 0 ? "#085041" : "#a32d2d" }}>Net <b>{fmt(jobNet)}</b></span>
                    </div>
                  </div>
                );
                return (
                  <div key={job.key} style={{
                    display: "grid", gridTemplateColumns: "70px 120px 1fr 90px 90px 90px 70px",
                    padding: "8px 14px", borderBottom: "1px solid #f0f0f0",
                    alignItems: "center", gap: 8, fontSize: 11,
                  }}>
                    <span style={{ color: "#888" }}>{formatDate(job.date)}</span>
                    <span style={{ fontWeight: 700, color: "#185FA5", overflowWrap: "anywhere" }}>{job.jobNumber || "—"}</span>
                    <div>
                      <div style={{ fontWeight: 500 }}>{job.brokerName || "—"}</div>
                      <div style={{ fontSize: 10, color: "#888" }}>{job.dispatcherName || "—"}</div>
                    </div>
                    <span>{fmt(job.billed)}</span>
                    <span style={{ color: "#854F0B" }}>{job.guardPaid ? fmt(job.guardPaid) : "—"}</span>
                    <span style={{ fontWeight: 600, color: jobNet >= 0 ? "#085041" : "#a32d2d" }}>{fmt(jobNet)}</span>
                    <span className={`chip ${statusChip[st]}`} style={{ fontSize: 9 }}>{st === "paid" ? "paid" : st === "sent" ? "owed" : "draft"}</span>
                  </div>
                );
              })}
            </div>

            {/* Breakdowns */}
            <div className="two-col">
              <div className="panel">
                <div className="panel-header"><div className="panel-title">By dispatcher</div></div>
                {Object.entries(byDispatcher).length === 0 ? (
                  <div style={{ padding: "14px", fontSize: 11, color: "#bbb" }}>No data</div>
                ) : Object.entries(byDispatcher).map(([name, d]) => (
                  <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderBottom: "1px solid #f8f8f8", fontSize: 11 }}>
                    <span style={{ flex: 1, fontWeight: 500 }}>{name}</span>
                    <span style={{ color: "#888" }}>{d.jobs} job{d.jobs !== 1 ? "s" : ""}</span>
                    <span style={{ fontWeight: 700, color: "#185FA5", width: 80, textAlign: "right" }}>{fmt(d.billed)}</span>
                  </div>
                ))}
              </div>
              <div className="panel">
                <div className="panel-header"><div className="panel-title">By guard</div></div>
                {Object.entries(byGuard).length === 0 ? (
                  <div style={{ padding: "14px", fontSize: 11, color: "#bbb" }}>No data</div>
                ) : Object.entries(byGuard).map(([name, d]) => (
                  <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderBottom: "1px solid #f8f8f8", fontSize: 11 }}>
                    <span style={{ flex: 1, fontWeight: 500 }}>{name}</span>
                    <span style={{ color: "#888" }}>{d.jobs} job{d.jobs !== 1 ? "s" : ""}{d.unpaid ? ` · ${d.unpaid} not paid yet` : ""}</span>
                    <span style={{ fontWeight: 700, color: "#854F0B", width: 80, textAlign: "right" }}>{fmt(d.paid)}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
